<template>
  <nav
    class="shadow-none navbar navbar-main navbar-expand-lg border-radius-xl"
    v-bind="$attrs"
    id="navbarBlur"
    data-scroll="true"
    :class="isAbsolute ? 'mt-4' : 'mt-0'"
  >
    <div class="px-3 py-1 container-fluid">
      <breadcrumbs :currentPage="currentRouteName" :color="color" />

      <div
        class="mt-2 collapse navbar-collapse mt-sm-0 me-md-0 me-sm-4"
        :class="isRTL ? 'px-0' : 'me-sm-4'"
        id="navbar"
      >
        <!-- Logout -->
        <div
          class="pe-md-3 d-flex flex-column align-items-center"
          :class="isRTL ? 'me-md-auto' : 'ms-md-auto'"
        >
          <a
            href="#"
            class="nav-link lh-1"
            :class="color || 'text-body'"
            title="Logout"
            @click.prevent="handleLogout"
          >
            <i class="material-icons">logout</i>
          </a>
          <span class="text-xs mt-1" :class="color || 'text-body'">Logout</span>
        </div>

        <ul class="navbar-nav justify-content-end">
          <!-- Sign out -->
          <li class="nav-item d-flex flex-column align-items-center">
            <router-link
              :to="{ name: 'SignIn' }"
              class="px-0 nav-link font-weight-bold lh-1"
              :class="color || 'text-body'"
            >
              <i
                class="material-icons"
                :class="isRTL ? 'ms-sm-2' : 'me-sm-1'"
                >account_circle</i
              >
            </router-link>
            <span class="text-xs mt-1" :class="color || 'text-body'">{{ username }}</span>
          </li>

          <!-- Sidebar toggle (mobile) -->
          <li class="nav-item d-xl-none ps-3 d-flex align-items-center">
            <a
              href="#"
              @click="toggleSidebar"
              class="p-0 nav-link text-body lh-1"
              id="iconNavbarSidenav"
            >
              <div class="sidenav-toggler-inner">
                <i class="sidenav-toggler-line"></i>
                <i class="sidenav-toggler-line"></i>
                <i class="sidenav-toggler-line"></i>
              </div>
            </a>
          </li>

          <!-- 🌍 Language selector -->
          <li class="nav-item d-flex align-items-center ms-3">
            <select v-model="$i18n.locale" class="form-select form-select-sm">
              <option value="en">EN</option>
              <option value="fr">FR</option>
            </select>
          </li>

          <!-- 🔔 Notifications -->
          <li
            class="nav-item dropdown d-flex align-items-center"
            :class="isRTL ? 'ps-2' : 'pe-2'"
          >
            <a
              href="#"
              class="p-0 nav-link lh-1"
              :class="[color || 'text-body', showMenu ? 'show' : '']"
              id="dropdownMenuButton"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              @click="onNotifClick"
            >
              <div class="position-relative">
                <i class="material-icons cursor-pointer">notifications</i>
                <span
                  v-if="unreadCount > 0"
                  class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style="font-size: 10px;"
                >
                  {{ unreadCount }}
                </span>
              </div>
            </a>

            <ul
              class="px-2 py-3 dropdown-menu dropdown-menu-end me-sm-n4"
              :class="showMenu ? 'show' : ''"
              aria-labelledby="dropdownMenuButton"
              style="width: 300px; max-height: 400px; overflow-y: auto;"
            >
              <li class="text-center mb-2">
                <button class="btn btn-outline-danger btn-sm" @click="markAll">
                  TOUT MARQUER COMME LU
                </button>
              </li>

              <li
                v-if="notifications.length === 0"
                class="text-center text-muted"
              >
                Aucune notification
              </li>

              <li
                v-for="notif in notifications"
                :key="notif.id"
                @click="goToNotif(notif)"
                class="dropdown-item d-flex align-items-center"
                style="cursor: pointer;"
              >
                <i
                  class="material-icons me-2"
                  :class="notif.read_at ? 'text-muted' : 'text-danger'"
                  style="font-size: 16px"
                >
                  {{ notif.read_at ? "done" : "fiber_manual_record" }}
                </i>
                <span
                  class="text-sm"
                  :class="{ 'text-muted': notif.read_at }"
                >
                  {{ notif.data.message }}
                </span>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script>
import Breadcrumbs from "../Breadcrumbs.vue";
import { mapMutations, mapState } from "vuex";
import authService from "@/services/authService";
import notificationService from "@/services/notificationService";

export default {
  name: "Navbar",
  components: { Breadcrumbs },
  props: ["minNav", "color"],
  data() {
    return {
      showMenu: false,
      notifications: [],
      unreadCount: 0,
      loading: false,
      intervalId: null,
    };
  },
  computed: {
    ...mapState(["isRTL", "isAbsolute"]),
    currentRouteName() {
      return this.$route.name;
    },
    username() {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user.firstname && user.lastname) {
        return `${user.firstname} ${user.lastname}`;
      }
      return user.email || user.username || 'User';
    },
  },
  methods: {
    ...mapMutations(["navbarMinimize", "toggleConfigurator"]),

    toggleSidebar() {
      this.navbarMinimize();
    },

    async handleLogout() {
      try {
        await authService.logout();
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        this.$router.push({ name: "SignIn" });
      } catch (error) {
        console.error("Logout failed", error);
      }
    },

    // 👉 Appelé au clic sur la cloche
    onNotifClick() {
      this.showMenu = !this.showMenu;
      if (this.showMenu) {
        this.fetchNotifications();
      }
    },

    async fetchNotifications() {
      try {
        this.loading = true;
        this.notifications = await notificationService.getAll();
        this.unreadCount = await notificationService.getUnreadCount();
      } catch (e) {
        console.error("Erreur fetchNotifications:", e);
      } finally {
        this.loading = false;
      }
    },

    async markAll() {
      try {
        await notificationService.markAllAsRead();
        this.fetchNotifications();
      } catch (e) {
        console.error("Erreur markAllAsRead:", e);
      }
    },

    async goToNotif(notif) {
      const type = notif.type;
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const role = Array.isArray(user.roles)
        ? user.roles[0]?.toLowerCase()
        : null;

      if (!notif.read_at) {
        try {
          await notificationService.markAsRead(notif.id);
        } catch (err) {
          console.warn("Erreur markAsRead :", err);
        }
      }

      if (type.includes("Loan")) {
        this.$router.push({
          name: role === "admin" ? "LoanReview" : "LoanRequest",
        });
      } else if (type.includes("Leave")) {
        this.$router.push({
          name: role === "admin" ? "LeaveReview" : "LeaveRequest",
        });
      } else if (type.includes("Certificate")) {
        this.$router.push({
          name: role === "admin" ? "CertificateReview" : "CertificateRequest",
        });
      } else if (type.includes("SalaryAdvance")) {
        this.$router.push({
          name: role === "admin" ? "SalaryAdvanceReview" : "SalaryAdvanceRequest",
        });
      } else {
        this.$router.push({ name: "Dashboard" });
      }

      this.fetchNotifications();
      this.showMenu = false;
    },
  },
  mounted() {
    this.fetchNotifications();
    this.intervalId = setInterval(() => this.fetchNotifications(), 30000);
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  },
};
</script>
